/**
 * G-10 (AIVA-8913) — whether a shape with text is selected in PowerPoint, kept
 * live for the action bar's Replace selection (SC-03).
 *
 * The bar used to be built once, from the selection read when the turn was
 * sent, so selecting a shape after the reply arrived never enabled it. Per the
 * spec's approved closure this subscribes ONCE to DocumentSelectionChanged
 * and re-reads on each event — no polling. The caller applies the value to the
 * most recent bar only.
 *
 * `null` until the first read lands (and whenever `enabled` is false), so the
 * caller keeps the send-time state instead of flashing a guess.
 */

import { useEffect, useState } from "react";
import { isTextSelectionPresent } from "../services/readSlideContext";

/** PowerPoint fires a burst of events while the user drags a selection. */
const SELECTION_DEBOUNCE_MS = 150;

export function usePptTextSelection(
  enabled: boolean,
  read: () => Promise<boolean> = isTextSelectionPresent,
): boolean | null {
  const [selected, setSelected] = useState<boolean | null>(null);

  useEffect(() => {
    if (!enabled) {
      setSelected(null);
      return;
    }
    // Reads are async and can land out of order; only the newest may commit.
    let run = 0;
    let disposed = false;
    let timer: ReturnType<typeof setTimeout> | null = null;

    const refresh = () => {
      const mine = ++run;
      void read().then((next) => {
        if (!disposed && mine === run) setSelected(next);
      });
    };
    const onSelectionChanged = () => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(refresh, SELECTION_DEBOUNCE_MS);
    };

    refresh();
    try {
      Office.context.document.addHandlerAsync(Office.EventType.DocumentSelectionChanged, onSelectionChanged);
    } catch {
      /* not supported in this host: the send-time state stands */
    }

    return () => {
      disposed = true;
      if (timer) clearTimeout(timer);
      try {
        Office.context.document.removeHandlerAsync(Office.EventType.DocumentSelectionChanged, {
          handler: onSelectionChanged,
        } as Office.RemoveHandlerOptions);
      } catch {
        /* ignore */
      }
    };
  }, [enabled, read]);

  return selected;
}
